export enum FontWeight {
    BOLD = "bold",
    NORMAL = "normal",
}

type GraphSize =
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "xxl"
    | "compare-primary"
    | "compare-secondary";

export type ConfigKey = keyof typeof graphConfig.size[GraphSize];

export const graphConfig: {
    size: Record<
        GraphSize,
        {
            chart: { width?: number; height?: number; label: boolean };
            axis: { fontSize: number; weight: FontWeight };
            limits: { fontSize: number; weight: FontWeight };
            texts: { fontSize: number; weight: FontWeight; className: string };
            ticks?: { fontSize: number; weight: FontWeight };
        }
    >;
    position: Record<GraphSize, { top: string; bottom: string }>;
    dot: Record<GraphSize, { top: string; left: string; size: string }>;
} = {
    size: {
        // inbar 12, wasserman 9
        sm: {
            chart: {
                label: false,
            },
            axis: {
                fontSize: 12,
                weight: FontWeight.BOLD,
            },
            limits: {
                fontSize: 11,
                weight: FontWeight.NORMAL,
            },
            texts: {
                fontSize: 11,
                weight: FontWeight.NORMAL,
                className: "w-2.5 text-[10px] xl:w-3 xl:text-xs font-bold",
            },
        },
        // inbar 4
        md: {
            chart: {
                width: 543,
                height: 301,
                label: false,
            },
            axis: {
                fontSize: 14,
                weight: FontWeight.BOLD,
            },
            limits: {
                fontSize: 12,
                weight: FontWeight.NORMAL,
            },
            ticks: {
                fontSize: 12,
                weight: FontWeight.NORMAL,
            },
            texts: {
                fontSize: 16,
                weight: FontWeight.NORMAL,
                className: "w-2.5 text-sm xl:w-3 xl:text-base font-bold",
            },
        },
        // anaerobic
        lg: {
            chart: {
                width: 542,
                height: 342,
                label: false,
            },
            axis: {
                fontSize: 14,
                weight: FontWeight.BOLD,
            },
            limits: {
                fontSize: 11,
                weight: FontWeight.NORMAL,
            },
            texts: {
                fontSize: 14,
                weight: FontWeight.NORMAL,
                className: "w-2.5 text-xs xl:w-3 xl:text-sm font-bold",
            },
        },
        // interpretation
        xl: {
            chart: {
                width: 525,
                height: 270,
                label: false,
            },
            axis: {
                fontSize: 17.79,
                weight: FontWeight.BOLD,
            },
            limits: {
                fontSize: 11,
                weight: FontWeight.NORMAL,
            },
            texts: {
                fontSize: 16.31,
                weight: FontWeight.NORMAL,
                className: "w-2.5 text-[10px] xl:w-3 xl:text-xs font-bold",
            },
        },
        // anaerobic compare main graph
        "compare-primary": {
            chart: {
                width: 705,
                height: 362,
                label: false,
            },
            axis: {
                fontSize: 18.25,
                weight: FontWeight.BOLD,
            },
            limits: {
                fontSize: 15,
                weight: FontWeight.NORMAL,
            },
            texts: {
                fontSize: 18.25,
                weight: FontWeight.NORMAL,
                className: "w-4 text-lg xl:w-[18px] font-bold",
            },
        },
        // anaerobic compare secondary graph
        "compare-secondary": {
            chart: {
                label: false,
            },
            axis: {
                fontSize: 18.25,
                weight: FontWeight.BOLD,
            },
            limits: {
                fontSize: 15,
                weight: FontWeight.NORMAL,
            },
            texts: {
                fontSize: 18.25,
                weight: FontWeight.NORMAL,
                className: "w-4 text-base xl:w-[18px] xl:text-lg font-bold",
            },
        },
        // full page
        xxl: {
            chart: {
                label: false,
            },
            axis: {
                fontSize: 27.09,
                weight: FontWeight.BOLD,
            },
            limits: {
                fontSize: 15,
                weight: FontWeight.NORMAL,
            },
            texts: {
                fontSize: 24.83,
                weight: FontWeight.NORMAL,
                className: "w-4 text-lg xl:text-2xl font-bold",
            },
        },
    },
    position: {
        sm: {
            top: "25px",
            bottom: "38px",
        },
        md: {
            top: "20px",
            bottom: "62px",
        },
        lg: {
            top: "17px",
            bottom: "42px",
        },
        xl: {
            top: "20px",
            bottom: "60px",
        },
        xxl: {
            top: "26px",
            bottom: "75px",
        },
        "compare-primary": {
            top: "20px",
            bottom: "54px",
        },
        "compare-secondary": {
            top: "20px",
            bottom: "54px",
        },
    },
    dot: {
        sm: {
            top: "3px",
            left: "-18px", // -(its size + 6px)
            size: "12px",
        },
        md: {
            top: "7px",
            left: "-26px", // -(its size + 12px)
            size: "14px",
        },
        lg: {
            top: "3px",
            left: "-28px",
            size: "16px",
        },
        xl: {
            top: "8px",
            left: "-28px",
            size: "16px",
        },
        xxl: {
            top: "10px",
            left: "-34px",
            size: "22px",
        },
        "compare-primary": {
            top: "6px",
            left: "-28px",
            size: "16px",
        },
        "compare-secondary": {
            top: "6px",
            left: "-28px",
            size: "16px",
        },
    },
};

export type ChartType = {
    scales: {
        x: {
            getPixelForValue: (value: number) => number;
            getValueForPixel: (pixel: number) => number;
        };
        y1: {
            getPixelForValue: (value: number) => number;
            getValueForPixel: (pixel: number) => number;
        };
        y2: {
            getPixelForValue: (value: number) => number;
            getValueForPixel: (pixel: number) => number;
        };
        y3: {
            getPixelForValue: (value: number) => number;
            getValueForPixel: (pixel: number) => number;
        };
    };
    options: {
        plugins: {
            annotation: {
                annotations: {
                    lowerLimit: { xMin: number; xMax: number };
                    upperLimit: { xMin: number; xMax: number };
                    vat: { xMin: number; xMax: number };
                    rcp: { xMin: number; xMax: number };
                    maxPoint1: { xValue: number; yValue: number };
                    maxPoint2: { xValue: number; yValue: number };
                };
            };
        };
    };
    canvas: HTMLCanvasElement;
    update: () => void;
    width: number;
    height: number;
    getElementAtEvent: (e: any) => any;
};

export const DEFAULT_LINE1_COLOR_ANAEROBIC = "#0F7EFF";
export const DEFAULT_LINE2_COLOR_ANAEROBIC = "#F910E2";
export const DEFAULT_LINE3_COLOR_ANAEROBIC = "#F301E1";
export const DEFAULT_LINE1_COLOR_WASSERMAN = "#666666";
export const DEFAULT_LINE2_COLOR_WASSERMAN = "#0F7EFF";
export const DEFAULT_LINE3_COLOR_WASSERMAN = "#F301E1";

export const TREND_1_DATASET_INDEX = 0;
export const TREND_1B_DATASET_INDEX = 1;
export const TREND_2B_DATASET_INDEX = 2;
export const TREND_2_DATASET_INDEX = 3;
export const RESPONSE_1_DATASET_INDEX = 4;
export const RESPONSE_2_DATASET_INDEX = 5;
export const RESPONSE_3_DATASET_INDEX = 6;
